import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { useAppointments } from "./appointments.hooks";
import AppointmentStatusBadge from "./components/AppointmentStatusBadge";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import BookingSheet from "@/app/pmc/appointments/components/BookingSheet.tsx";
import ActionsDropdown from "@/app/pmc/appointments/components/ActionsDropdown.tsx";
import BulkUploadModal from "./components/BulkUploadModal";
import AppointmentQuickViewSheet from "./components/AppointmentQuickViewSheet";
import { Button } from "@/components/ui/button";
import { Upload, Eye } from "lucide-react";
import moment from "moment";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";

const isDay = (value: string | null): value is string => value !== null && moment(value, "YYYY-MM-DD", true).isValid();

export default function AppointmentsPage() {
  const { list, total, filters, options, pagination, openSheet, sheetOpen, refetch } = useAppointments();
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [quickViewAppt, setQuickViewAppt] = useState<AppointmentJoin | null>(null);

  // ── URL filter persistence ─────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  // On mount: hydrate Redux filters from URL params
  useEffect(() => {
    const q       = searchParams.get("q");
    const status  = searchParams.get("status");
    const service = searchParams.get("service");
    const from    = searchParams.get("from");
    const to      = searchParams.get("to");
    const vendor  = searchParams.get("vendor");

    if (q)       filters.setSearch(q);
    if (status)  filters.setStatus(status);
    if (service) filters.setServiceParent(service);
    if (isDay(from)) filters.setDateFrom(from);
    if (isDay(to))   filters.setDateTo(to);
    if (vendor)  filters.setVendor(vendor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync whenever filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search)        params.set("q",       filters.search);
    if (filters.status)        params.set("status",  filters.status);
    if (filters.serviceParent) params.set("service", filters.serviceParent);
    if (filters.dateFrom)      params.set("from",    filters.dateFrom);
    if (filters.dateTo)        params.set("to",      filters.dateTo);
    if (filters.vendor)        params.set("vendor",  filters.vendor);
    setSearchParams(params, { replace: true });
  }, [
    filters.search,
    filters.status,
    filters.serviceParent,
    filters.dateFrom,
    filters.dateTo,
    filters.vendor,
  ]);

  // Convert date strings to Date objects for the filter component using moment
  const dateFrom: Date | undefined = useMemo(() => {
    return filters.dateFrom ? moment(filters.dateFrom, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateFrom]);

  const dateTo: Date | undefined = useMemo(() => {
    return filters.dateTo ? moment(filters.dateTo, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateTo]);

  const handleDateFromChange = (date: Date | undefined) => {
    filters.setDateFrom(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleDateToChange = (date: Date | undefined) => {
    filters.setDateTo(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  // Date validation: if to date is selected, from date cannot be after it
  const getDisabledDatesForFrom = (date: Date): boolean => {
    if (dateTo) {
      return moment(date).isAfter(moment(dateTo), "day");
    }
    return false;
  };

  // Date validation: if from date is selected, to date cannot be before it
  const getDisabledDatesForTo = (date: Date): boolean => {
    if (dateFrom) {
      return moment(date).isBefore(moment(dateFrom), "day");
    }
    return false;
  };

  /* === filters for SimpleTable === */
  const filterItems: FilterItem[] = useMemo(
    () => [
      {
        type: "search",
        title: "Search by property, unit, vendor, or service...",
        value: filters.search,
        onValueChange: filters.setSearch,
        grid: 3,
      },
      {
        type: "date",
        title: "From Date",
        value: dateFrom,
        onValueChange: handleDateFromChange,
        grid: 2,
        disabledDates: getDisabledDatesForFrom,
      },
      {
        type: "date",
        title: "To Date",
        value: dateTo,
        onValueChange: handleDateToChange,
        grid: 2,
        disabledDates: getDisabledDatesForTo,
      },
      {
        type: "select",
        title: "Status",
        value: filters.status,
        onValueChange: filters.setStatus,
        options: [
          { value: "pending", label: "Pending" },
          { value: "assigned", label: "Assigned" },
          { value: "accepted", label: "Accepted" },
          { value: "inprogress", label: "In Progress" },
          { value: "goback", label: "Go Back" },
          { value: "rescheduled", label: "Rescheduled" },
          { value: "inspected", label: "Inspected" },
          { value: "done", label: "Done" },
          { value: "completed", label: "Completed" },
          { value: "cancelled", label: "Cancelled" },
        ],
        grid: 2,
      },
      {
        type: "select",
        title: "Service",
        value: filters.serviceParent,
        onValueChange: filters.setServiceParent,
        options: options.serviceParents.map((p) => ({ label: p, value: p })),
        grid: 2,
      },
    ],
    [
      filters.search,
      filters.status,
      filters.serviceParent,
      filters.vendor,
      filters.assignee,
      filters.dateFrom,
      filters.dateTo,
      options.serviceParents,
      options.vendorNames,
      options.assigneeNames,
      dateFrom,
      dateTo,
    ]
  );

  /* === columns === */
  const cols = useMemo<ColumnDef<AppointmentJoin>[]>(
    () => [
      {
        header: "ID",
        accessor: (r) => {
          const idDisplay = r.id.slice(0, 8).toUpperCase();
          const handleCopy = async (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(r.id);
              toast.success("ID copied to clipboard");
            } catch (error) {
              toast.error("Failed to copy ID");
            }
          };
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCopy}
                  className="font-mono cursor-pointer hover:underline text-left"
                >
                  {idDisplay}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to copy</p>
              </TooltipContent>
            </Tooltip>
          );
        },
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Apartment",
        accessor: (r) => (
          <Link to={`/appointments/${r.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.property.name}</span>
            <span className="text-sm text-muted-foreground">
              Unit {r.property.unit_number} • Beds {r.property.beds} • Baths {r.property.baths}
            </span>
          </Link>
        ),
        sortable: true,
        sortKey: "properties.name",
        cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
      },
      {
        header: "Vendors",
        accessor: (r) => <div className="text-sm text-muted-foreground">{r.vendor.name}</div>,
      },
      {
        header: "Service",
        accessor: (r) => (
          <>
            <div className="font-medium">
              {r.service.name} - {r.final_price ? `$${r.final_price.toFixed(2)}` : "Price TBD"}
            </div>
            <div className="text-sm text-muted-foreground">{r.service.parent_name}</div>
          </>
        ),
      },
      {
        header: "Created",
        accessor: (r) => moment(r.created_at).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "created_at",
      },
      {
        header: "Scheduled",
        accessor: (r) => moment(r.date).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "date",
      },
      {
        header: "Status",
        accessor: (r) => <AppointmentStatusBadge status={r.status} />,
      },
      {
        header: "Actions",
        accessor: (row) => (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuickViewAppt(row);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Quick view</TooltipContent>
            </Tooltip>
            <ActionsDropdown row={row} />
          </div>
        ),
        className: "whitespace-nowrap",
      },
    ],
    [setQuickViewAppt]
  );

  return (
    <MainLayout
      breadcrumb={[{ name: "Appointments", href: "#" }]}
      pageHeader={{
        title: "Appointments",
        subtitle: "Manage your appointments here",
        buttonText: "New Booking",
        onButtonClick: () => openSheet(),
        additionalButtons: (
          <Button size="lg" variant="outline" onClick={() => setBulkUploadOpen(true)} className="ml-2 uppercase">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
        ),
      }}
    >
      <SimpleTable<AppointmentJoin>
        data={list}
        totalCount={total}
        columns={cols}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        onReset={() => {
          filters.setStatus("");
          filters.setServiceParent("");
          filters.setSearch("");
          filters.setVendor("");
          filters.setAssignee("");
          filters.setDateFrom("");
          filters.setDateTo("");
        }}
      />

      {sheetOpen && <BookingSheet />}

      {bulkUploadOpen && (
        <BulkUploadModal
          isOpen={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onUploadComplete={refetch}
        />
      )}

      <AppointmentQuickViewSheet
        appointment={quickViewAppt}
        open={!!quickViewAppt}
        onClose={() => setQuickViewAppt(null)}
      />
    </MainLayout>
  );
}
